import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, vModelText as _vModelText, withDirectives as _withDirectives, unref as _unref, vModelSelect as _vModelSelect, vModelDynamic as _vModelDynamic, vShow as _vShow, withModifiers as _withModifiers, TransitionGroup as _TransitionGroup, withCtx as _withCtx, createBlock as _createBlock, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "space-y-6" }
const _hoisted_2 = { class: "flex items-center justify-between" }
const _hoisted_3 = {
  key: 0,
  class: "flex flex-col items-center justify-center rounded-lg border border-gray-700 bg-dark-secondary py-12"
}
const _hoisted_4 = ["onDragstart", "onDragover"]
const _hoisted_5 = { class: "flex items-center border-b border-gray-700 p-4" }
const _hoisted_6 = { class: "flex items-center gap-3 flex-1 min-w-0" }
const _hoisted_7 = { class: "flex items-center gap-2" }
const _hoisted_8 = ["title"]
const _hoisted_9 = ["onClick", "title"]
const _hoisted_10 = { class: "h-5 w-5" }
const _hoisted_11 = {
  key: 0,
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 20 20",
  fill: "currentColor",
  class: "text-emerald-500"
}
const _hoisted_12 = {
  key: 1,
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 20 20",
  fill: "currentColor",
  class: "text-gray-500"
}
const _hoisted_13 = { class: "flex items-center gap-2 -ml-2" }
const _hoisted_14 = ["onClick"]
const _hoisted_15 = ["onUpdate:modelValue", "onChange", "onBlur"]
const _hoisted_16 = { class: "flex items-center gap-2 ml-auto" }
const _hoisted_17 = { class: "flex items-center gap-2" }
const _hoisted_18 = ["onClick", "disabled"]
const _hoisted_19 = {
  key: 0,
  class: "h-4 w-4 animate-spin",
  fill: "none",
  viewBox: "0 0 24 24"
}
const _hoisted_20 = ["onClick"]
const _hoisted_21 = ["onClick"]
const _hoisted_22 = { class: "flex items-center gap-4 border-b border-gray-700 p-4 bg-gray-800/30" }
const _hoisted_23 = { class: "flex items-center gap-2" }
const _hoisted_24 = { class: "flex rounded-lg border border-gray-700 p-1" }
const _hoisted_25 = ["onClick"]
const _hoisted_26 = { class: "flex items-center gap-2" }
const _hoisted_27 = { class: "flex rounded-lg border border-gray-700 p-1" }
const _hoisted_28 = ["onClick"]
const _hoisted_29 = { class: "divide-y divide-gray-700" }
const _hoisted_30 = { class: "w-48" }
const _hoisted_31 = ["onUpdate:modelValue", "onChange"]
const _hoisted_32 = ["value"]
const _hoisted_33 = { class: "w-48" }
const _hoisted_34 = ["onUpdate:modelValue"]
const _hoisted_35 = ["value"]
const _hoisted_36 = { class: "flex-1" }
const _hoisted_37 = ["onUpdate:modelValue"]
const _hoisted_38 = ["value"]
const _hoisted_39 = ["onUpdate:modelValue"]
const _hoisted_40 = ["value"]
const _hoisted_41 = ["onUpdate:modelValue", "type", "placeholder"]
const _hoisted_42 = ["onClick"]
const _hoisted_43 = {
  key: 0,
  class: "flex flex-col items-center justify-center py-8 text-center"
}
const _hoisted_44 = { class: "mt-4" }
const _hoisted_45 = ["onClick"]
const _hoisted_46 = {
  key: 0,
  class: "flex flex-col items-center justify-center rounded-lg border border-gray-700 bg-dark-secondary py-12"
}
const _hoisted_47 = { class: "mt-6" }
const _hoisted_48 = ["disabled"]

import { ref, computed, onMounted, watch } from 'vue'
import { useRulesStore } from '@/stores/rules'
import { FIELD_LABELS, OPERATOR_LABELS, FIELD_OPERATORS, LOGICAL_OPERATOR_LABELS } from '@/types/rules'
import type { RuleField, LogicalOperator, RuleGroup } from '@/types/rules'
import BaseModal from '@/components/ui/BaseModal.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'RulesEngine',
  setup(__props) {

const rulesStore = useRulesStore()
const showNewGroupModal = ref(false)
const newGroupName = ref('')

// Get loading state from store
const isLoading = computed(() => rulesStore.loading)

// Initialize with all groups collapsed
const collapsedGroups = ref<Set<string>>(new Set())
const draggingGroup = ref<RuleGroup | null>(null)
const dragTarget = ref<RuleGroup | null>(null)

const ruleGroups = computed(() => rulesStore.sortedRuleGroups)

// Watch for changes in ruleGroups and ensure new groups are collapsed by default
watch(ruleGroups, (newGroups) => {
  newGroups.forEach(group => {
    if (!collapsedGroups.value.has(group.id)) {
      collapsedGroups.value.add(group.id)
    }
  })
}, { immediate: true })

// Drag and drop handlers
const dragStart = (event: DragEvent, group: RuleGroup) => {
  if (!event.dataTransfer) return
  draggingGroup.value = group
  event.dataTransfer.effectAllowed = 'move'
}

const dragEnd = () => {
  draggingGroup.value = null
  dragTarget.value = null
}

const dragOver = (event: DragEvent, targetGroup: RuleGroup) => {
  if (!draggingGroup.value || draggingGroup.value.id === targetGroup.id) return
  dragTarget.value = targetGroup
}

const isDragTarget = (group: RuleGroup): boolean => {
  return dragTarget.value?.id === group.id
}

// Reorder groups and update orders
const updateGroupOrders = async () => {
  if (!draggingGroup.value || !dragTarget.value) return

  const groups = [...ruleGroups.value]
  const sourceIndex = groups.findIndex(g => g.id === draggingGroup.value?.id)
  const targetIndex = groups.findIndex(g => g.id === dragTarget.value?.id)
  
  if (sourceIndex === -1 || targetIndex === -1) return

  // Remove the dragged group
  const [movedGroup] = groups.splice(sourceIndex, 1)
  // Insert it at the new position
  groups.splice(targetIndex, 0, movedGroup)

  // Update orders
  groups.forEach((group, index) => {
    group.order = index
  })

  // Save all groups with new orders
  try {
    await Promise.all(groups.map(group => rulesStore.saveRuleGroup(group.id)))
  } catch (error) {
    console.error('Failed to update group orders:', error)
  }
}

const toggleGroupCollapse = (groupId: string) => {
  if (collapsedGroups.value.has(groupId)) {
    collapsedGroups.value.delete(groupId)
  } else {
    collapsedGroups.value.add(groupId)
  }
}

const createRuleGroup = async () => {
  if (newGroupName.value.trim()) {
    const result = await rulesStore.addRuleGroup(newGroupName.value.trim(), 'active')
    // New groups start expanded (not added to collapsedGroups)
    newGroupName.value = ''
    showNewGroupModal.value = false
  }
}

const addRule = (groupId: string) => {
  collapsedGroups.value.delete(groupId);
  rulesStore.addRule(groupId, {
    field: 'inventory_qty',
    operator: FIELD_OPERATORS.inventory_qty[0],
    value: ''
  })
}

const updateOperatorForField = (groupId: string, ruleIndex: number, field: RuleField) => {
  // When field changes, set the operator to the first valid operator for that field
  rulesStore.updateRule(groupId, ruleIndex, {
    operator: FIELD_OPERATORS[field][0]
  })
}

const updateRuleGroup = (groupId: string, updates: Partial<RuleGroup>) => {
  rulesStore.updateRuleGroup(groupId, updates)
}

const deleteRule = (groupId: string, ruleIndex: number) => {
  rulesStore.deleteRule(groupId, ruleIndex)
}

const deleteRuleGroup = (groupId: string) => {
  rulesStore.deleteRuleGroup(groupId)
}

const toggleRuleGroup = (groupId: string) => {
  rulesStore.toggleRuleGroup(groupId)
}

const hasUnsavedChanges = (groupId: string): boolean => {
  return rulesStore.hasUnsavedChanges(groupId)
}

const isSaving = (groupId: string): boolean => {
  return rulesStore.isSaving(groupId)
}

const isSaveSuccess = (groupId: string): boolean => {
  return rulesStore.isSaveSuccess(groupId)
}

const saveRuleGroup = async (groupId: string) => {
  try {
    await rulesStore.saveRuleGroup(groupId)
  } catch (error) {
    console.error('Failed to save rule group:', error)
  }
}

const getValuePlaceholder = (field: RuleField): string => {
  switch (field) {
    case 'inventory_qty':
      return 'Enter quantity'
    case 'price':
      return 'Enter price'
    case 'product_type':
      return 'Enter product type'
    case 'tags':
      return 'Enter tag'
    case 'title':
      return 'Enter title'
    default:
      return 'Enter value'
  }
}

// Load rules when component mounts
onMounted(async () => {
  try {
    await rulesStore.loadRules()
  } catch (error) {
    console.error('Failed to load rules:', error)
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[7] || (_cache[7] = _createElementVNode("div", null, [
        _createElementVNode("h2", { class: "text-xl font-semibold text-gray-100" }, "Product Activation Rules"),
        _createElementVNode("p", { class: "mt-1 text-sm text-gray-400" }, [
          _createTextVNode(" Rules control product visibility. Rule groups run in order - drag to reorder priority."),
          _createElementVNode("br")
        ])
      ], -1)),
      _createElementVNode("button", {
        onClick: _cache[0] || (_cache[0] = ($event: any) => (showNewGroupModal.value = true)),
        class: "inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark"
      }, _cache[6] || (_cache[6] = [
        _createElementVNode("svg", {
          xmlns: "http://www.w3.org/2000/svg",
          class: "h-5 w-5",
          viewBox: "0 0 20 20",
          fill: "currentColor"
        }, [
          _createElementVNode("path", {
            "fill-rule": "evenodd",
            d: "M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z",
            "clip-rule": "evenodd"
          })
        ], -1),
        _createTextVNode(" Add Rule Group ")
      ]))
    ]),
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, _cache[8] || (_cache[8] = [
          _createElementVNode("svg", {
            class: "h-12 w-12 text-gray-400 animate-spin",
            viewBox: "0 0 24 24"
          }, [
            _createElementVNode("circle", {
              class: "opacity-25",
              cx: "12",
              cy: "12",
              r: "10",
              stroke: "currentColor",
              "stroke-width": "4",
              fill: "none"
            }),
            _createElementVNode("path", {
              class: "opacity-75",
              fill: "currentColor",
              d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            })
          ], -1),
          _createElementVNode("h3", { class: "mt-2 text-sm font-medium text-gray-200" }, "Loading rules...", -1)
        ])))
      : (_openBlock(), _createBlock(_TransitionGroup, {
          key: 1,
          tag: "div",
          class: "space-y-4",
          name: "rule-group"
        }, {
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(ruleGroups.value, (group, index) => {
              return (_openBlock(), _createElementBlock("div", {
                key: group.id,
                draggable: "true",
                onDragstart: ($event: any) => (dragStart($event, group)),
                onDragend: dragEnd,
                onDragover: _withModifiers(($event: any) => (dragOver($event, group)), ["prevent"]),
                onDragenter: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent"])),
                onDrop: _withModifiers(updateGroupOrders, ["prevent"]),
                class: _normalizeClass(["rounded-lg border border-gray-700 bg-dark-secondary transition-all duration-200", { 
          'opacity-50': draggingGroup.value?.id === group.id, 
          'border-primary shadow-lg': isDragTarget(group),
          'transform scale-[0.98] bg-dark-secondary/80': draggingGroup.value?.id === group.id
        }])
              }, [
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("div", _hoisted_6, [
                    _createElementVNode("div", _hoisted_7, [
                      _createElementVNode("span", {
                        class: "text-sm font-medium text-gray-400",
                        title: 'Execution order: ' + (index + 1)
                      }, " #" + _toDisplayString(index + 1), 9, _hoisted_8),
                      _cache[9] || (_cache[9] = _createElementVNode("div", {
                        class: "cursor-move p-1 hover:bg-gray-700/50 rounded",
                        title: "Drag to reorder execution priority"
                      }, [
                        _createElementVNode("svg", {
                          xmlns: "http://www.w3.org/2000/svg",
                          class: "h-5 w-5 text-gray-400",
                          viewBox: "0 0 20 20",
                          fill: "currentColor"
                        }, [
                          _createElementVNode("path", { d: "M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14zm6-8a2 2 0 1 0-.001-4.001A2 2 0 0 0 13 6zm0 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8zm0 6a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14z" })
                        ])
                      ], -1))
                    ]),
                    _createElementVNode("button", {
                      onClick: ($event: any) => (toggleRuleGroup(group.id)),
                      class: "rounded-md p-1 hover:bg-gray-700/50",
                      title: group.isActive ? 'Disable group' : 'Enable group'
                    }, [
                      _createElementVNode("div", _hoisted_10, [
                        (group.isActive)
                          ? (_openBlock(), _createElementBlock("svg", _hoisted_11, _cache[10] || (_cache[10] = [
                              _createElementVNode("path", {
                                "fill-rule": "evenodd",
                                d: "M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z",
                                "clip-rule": "evenodd"
                              }, null, -1)
                            ])))
                          : (_openBlock(), _createElementBlock("svg", _hoisted_12, _cache[11] || (_cache[11] = [
                              _createElementVNode("path", {
                                "fill-rule": "evenodd",
                                d: "M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z",
                                "clip-rule": "evenodd"
                              }, null, -1)
                            ])))
                      ])
                    ], 8, _hoisted_9),
                    _createElementVNode("div", _hoisted_13, [
                      _createElementVNode("button", {
                        onClick: ($event: any) => (toggleGroupCollapse(group.id)),
                        class: "rounded hover:bg-gray-700/30 p-1"
                      }, [
                        (_openBlock(), _createElementBlock("svg", {
                          xmlns: "http://www.w3.org/2000/svg",
                          class: _normalizeClass(["h-5 w-5 text-gray-400 transition-transform", { 'rotate-90': !collapsedGroups.value.has(group.id) }]),
                          viewBox: "0 0 20 20",
                          fill: "currentColor"
                        }, _cache[12] || (_cache[12] = [
                          _createElementVNode("path", {
                            "fill-rule": "evenodd",
                            d: "M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z",
                            "clip-rule": "evenodd"
                          }, null, -1)
                        ]), 2))
                      ], 8, _hoisted_14),
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": ($event: any) => ((group.name) = $event),
                        onChange: ($event: any) => (updateRuleGroup(group.id, { name: group.name })),
                        onBlur: ($event: any) => (updateRuleGroup(group.id, { name: group.name })),
                        class: "bg-transparent text-lg font-medium text-gray-100 focus:outline-none focus:ring-1 focus:ring-primary rounded px-2 py-1 hover:bg-gray-700/30 w-80",
                        placeholder: 'Enter group name'
                      }, null, 40, _hoisted_15), [
                        [_vModelText, group.name]
                      ])
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_16, [
                    _createElementVNode("div", _hoisted_17, [
                      _createElementVNode("span", {
                        class: _normalizeClass([
                  'rounded-full px-2 py-1 text-xs font-medium',
                  group.isActive
                    ? 'bg-emerald-500/10 text-emerald-500'
                    : 'bg-gray-500/10 text-gray-500'
                ])
                      }, _toDisplayString(group.isActive ? 'Active' : 'Inactive'), 3),
                      _createElementVNode("span", {
                        class: _normalizeClass([
                  'rounded-full px-2 py-1 text-xs font-medium',
                  group.outcome === 'active'
                    ? 'bg-blue-500/10 text-blue-500'
                    : 'bg-red-500/10 text-red-500'
                ])
                      }, " Set products " + _toDisplayString(group.outcome), 3)
                    ]),
                    (hasUnsavedChanges(group.id))
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 0,
                          onClick: ($event: any) => (saveRuleGroup(group.id)),
                          disabled: isSaving(group.id),
                          class: _normalizeClass([
                'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                isSaveSuccess(group.id)
                  ? 'border-emerald-500 bg-emerald-500/10 text-emerald-500'
                  : 'border-primary bg-primary/10 text-primary hover:bg-primary/20',
                'disabled:opacity-50'
              ])
                        }, [
                          (isSaving(group.id))
                            ? (_openBlock(), _createElementBlock("svg", _hoisted_19, _cache[13] || (_cache[13] = [
                                _createElementVNode("circle", {
                                  class: "opacity-25",
                                  cx: "12",
                                  cy: "12",
                                  r: "10",
                                  stroke: "currentColor",
                                  "stroke-width": "4"
                                }, null, -1),
                                _createElementVNode("path", {
                                  class: "opacity-75",
                                  fill: "currentColor",
                                  d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                }, null, -1)
                              ])))
                            : _createCommentVNode("", true),
                          _createTextVNode(" " + _toDisplayString(isSaving(group.id) ? 'Saving...' : 'Save Changes'), 1)
                        ], 10, _hoisted_18))
                      : _createCommentVNode("", true),
                    _createElementVNode("button", {
                      onClick: ($event: any) => (addRule(group.id)),
                      class: "inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-3 py-1.5 text-sm font-medium text-gray-200 hover:border-primary/30 hover:bg-primary/10 hover:text-primary"
                    }, " Add Rule ", 8, _hoisted_20),
                    _createElementVNode("button", {
                      onClick: ($event: any) => (deleteRuleGroup(group.id)),
                      class: "rounded-lg p-2 text-gray-400 hover:bg-red-500/10 hover:text-red-500",
                      title: "Delete group"
                    }, _cache[14] || (_cache[14] = [
                      _createElementVNode("svg", {
                        xmlns: "http://www.w3.org/2000/svg",
                        class: "h-5 w-5",
                        viewBox: "0 0 20 20",
                        fill: "currentColor"
                      }, [
                        _createElementVNode("path", {
                          "fill-rule": "evenodd",
                          d: "M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z",
                          "clip-rule": "evenodd"
                        })
                      ], -1)
                    ]), 8, _hoisted_21)
                  ])
                ]),
                _withDirectives(_createElementVNode("div", null, [
                  _createElementVNode("div", _hoisted_22, [
                    _createElementVNode("div", _hoisted_23, [
                      _cache[15] || (_cache[15] = _createElementVNode("span", { class: "text-sm text-gray-400" }, "Combine rules with:", -1)),
                      _createElementVNode("div", _hoisted_24, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(LOGICAL_OPERATOR_LABELS), (label, operator) => {
                          return (_openBlock(), _createElementBlock("button", {
                            key: operator,
                            onClick: ($event: any) => (updateRuleGroup(group.id, { logicalOperator: operator })),
                            class: _normalizeClass([
                    'px-3 py-1 text-sm font-medium rounded transition-colors',
                    group.logicalOperator === operator
                      ? 'bg-primary text-white'
                      : 'text-gray-400 hover:bg-gray-700/50 hover:text-white'
                  ])
                          }, _toDisplayString(operator), 11, _hoisted_25))
                        }), 128))
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_26, [
                      _cache[16] || (_cache[16] = _createElementVNode("span", { class: "text-sm text-gray-400" }, "When rules match:", -1)),
                      _createElementVNode("div", _hoisted_27, [
                        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(['active', 'inactive'] as const, (outcome) => {
                          return _createElementVNode("button", {
                            key: outcome,
                            onClick: ($event: any) => (updateRuleGroup(group.id, { outcome })),
                            class: _normalizeClass([
                    'px-3 py-1 text-sm font-medium rounded transition-colors',
                    group.outcome === outcome
                      ? outcome === 'active' ? 'bg-blue-500 text-white' : 'bg-red-500 text-white'
                      : 'text-gray-400 hover:bg-gray-700/50 hover:text-white'
                  ])
                          }, " Set " + _toDisplayString(outcome), 11, _hoisted_28)
                        }), 64))
                      ])
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_29, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.rules, (rule, ruleIndex) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: ruleIndex,
                        class: "flex items-center gap-4 p-4"
                      }, [
                        _createElementVNode("div", _hoisted_30, [
                          _withDirectives(_createElementVNode("select", {
                            "onUpdate:modelValue": ($event: any) => ((rule.field) = $event),
                            onChange: ($event: any) => (updateOperatorForField(group.id, ruleIndex, rule.field)),
                            class: "block w-full rounded-lg border border-gray-700 bg-dark px-3 py-2 text-sm text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                          }, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(FIELD_LABELS), (label, field) => {
                              return (_openBlock(), _createElementBlock("option", {
                                key: field,
                                value: field
                              }, _toDisplayString(label), 9, _hoisted_32))
                            }), 128))
                          ], 40, _hoisted_31), [
                            [_vModelSelect, rule.field]
                          ])
                        ]),
                        _createElementVNode("div", _hoisted_33, [
                          _withDirectives(_createElementVNode("select", {
                            "onUpdate:modelValue": ($event: any) => ((rule.operator) = $event),
                            class: "block w-full rounded-lg border border-gray-700 bg-dark px-3 py-2 text-sm text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                          }, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(FIELD_OPERATORS)[rule.field], (operator) => {
                              return (_openBlock(), _createElementBlock("option", {
                                key: operator,
                                value: operator
                              }, _toDisplayString(_unref(OPERATOR_LABELS)[operator]), 9, _hoisted_35))
                            }), 128))
                          ], 8, _hoisted_34), [
                            [_vModelSelect, rule.operator]
                          ])
                        ]),
                        _createElementVNode("div", _hoisted_36, [
                          (rule.field === 'product_type')
                            ? _withDirectives((_openBlock(), _createElementBlock("select", {
                                key: 0,
                                "onUpdate:modelValue": ($event: any) => ((rule.value) = $event),
                                class: "block w-full rounded-lg border border-gray-700 bg-dark px-3 py-2 text-sm text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                              }, [
                                _cache[17] || (_cache[17] = _createElementVNode("option", { value: "" }, "Select Product Type", -1)),
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(rulesStore).availableProductTypes, (type) => {
                                  return (_openBlock(), _createElementBlock("option", {
                                    key: type,
                                    value: type
                                  }, _toDisplayString(type), 9, _hoisted_38))
                                }), 128))
                              ], 8, _hoisted_37)), [
                                [_vModelSelect, rule.value]
                              ])
                            : (rule.field === 'tags')
                              ? _withDirectives((_openBlock(), _createElementBlock("select", {
                                  key: 1,
                                  "onUpdate:modelValue": ($event: any) => ((rule.value) = $event),
                                  class: "block w-full rounded-lg border border-gray-700 bg-dark px-3 py-2 text-sm text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                                }, [
                                  _cache[18] || (_cache[18] = _createElementVNode("option", { value: "" }, "Select Tag", -1)),
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(rulesStore).availableTags, (tag) => {
                                    return (_openBlock(), _createElementBlock("option", {
                                      key: tag,
                                      value: tag
                                    }, _toDisplayString(tag), 9, _hoisted_40))
                                  }), 128))
                                ], 8, _hoisted_39)), [
                                  [_vModelSelect, rule.value]
                                ])
                              : _withDirectives((_openBlock(), _createElementBlock("input", {
                                  key: 2,
                                  "onUpdate:modelValue": ($event: any) => ((rule.value) = $event),
                                  type: rule.field === 'inventory_qty' || rule.field === 'price' ? 'number' : 'text',
                                  class: "block w-full rounded-lg border border-gray-700 bg-dark px-3 py-2 text-sm text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary",
                                  placeholder: getValuePlaceholder(rule.field)
                                }, null, 8, _hoisted_41)), [
                                  [_vModelDynamic, rule.value]
                                ])
                        ]),
                        _createElementVNode("button", {
                          onClick: ($event: any) => (deleteRule(group.id, ruleIndex)),
                          class: "rounded-lg p-2 text-gray-400 hover:bg-red-500/10 hover:text-red-500",
                          title: "Delete rule"
                        }, _cache[19] || (_cache[19] = [
                          _createElementVNode("svg", {
                            xmlns: "http://www.w3.org/2000/svg",
                            class: "h-5 w-5",
                            viewBox: "0 0 20 20",
                            fill: "currentColor"
                          }, [
                            _createElementVNode("path", { d: "M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" })
                          ], -1)
                        ]), 8, _hoisted_42)
                      ]))
                    }), 128)),
                    (group.rules.length === 0)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_43, [
                          _cache[21] || (_cache[21] = _createElementVNode("svg", {
                            xmlns: "http://www.w3.org/2000/svg",
                            class: "h-12 w-12 text-gray-400",
                            fill: "none",
                            viewBox: "0 0 24 24",
                            stroke: "currentColor"
                          }, [
                            _createElementVNode("path", {
                              "stroke-linecap": "round",
                              "stroke-linejoin": "round",
                              "stroke-width": "2",
                              d: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                            })
                          ], -1)),
                          _cache[22] || (_cache[22] = _createElementVNode("h3", { class: "mt-2 text-sm font-medium text-gray-200" }, "No rules yet", -1)),
                          _cache[23] || (_cache[23] = _createElementVNode("p", { class: "mt-1 text-sm text-gray-400" }, " Get started by adding a rule to this group ", -1)),
                          _createElementVNode("div", _hoisted_44, [
                            _createElementVNode("button", {
                              onClick: ($event: any) => (addRule(group.id)),
                              class: "inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark"
                            }, _cache[20] || (_cache[20] = [
                              _createElementVNode("svg", {
                                xmlns: "http://www.w3.org/2000/svg",
                                class: "h-5 w-5",
                                viewBox: "0 0 20 20",
                                fill: "currentColor"
                              }, [
                                _createElementVNode("path", {
                                  "fill-rule": "evenodd",
                                  d: "M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z",
                                  "clip-rule": "evenodd"
                                })
                              ], -1),
                              _createTextVNode(" Add First Rule ")
                            ]), 8, _hoisted_45)
                          ])
                        ]))
                      : _createCommentVNode("", true)
                  ])
                ], 512), [
                  [_vShow, !collapsedGroups.value.has(group.id)]
                ])
              ], 42, _hoisted_4))
            }), 128)),
            (!isLoading.value && ruleGroups.value.length === 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_46, [
                  _cache[25] || (_cache[25] = _createElementVNode("svg", {
                    xmlns: "http://www.w3.org/2000/svg",
                    class: "h-12 w-12 text-gray-400",
                    fill: "none",
                    viewBox: "0 0 24 24",
                    stroke: "currentColor"
                  }, [
                    _createElementVNode("path", {
                      "stroke-linecap": "round",
                      "stroke-linejoin": "round",
                      "stroke-width": "2",
                      d: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    })
                  ], -1)),
                  _cache[26] || (_cache[26] = _createElementVNode("h3", { class: "mt-2 text-sm font-medium text-gray-200" }, "No rule groups", -1)),
                  _cache[27] || (_cache[27] = _createElementVNode("p", { class: "mt-1 text-sm text-gray-400" }, " Get started by creating a new rule group ", -1)),
                  _createElementVNode("div", _hoisted_47, [
                    _createElementVNode("button", {
                      onClick: _cache[2] || (_cache[2] = ($event: any) => (showNewGroupModal.value = true)),
                      class: "inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark"
                    }, _cache[24] || (_cache[24] = [
                      _createElementVNode("svg", {
                        xmlns: "http://www.w3.org/2000/svg",
                        class: "h-5 w-5",
                        viewBox: "0 0 20 20",
                        fill: "currentColor"
                      }, [
                        _createElementVNode("path", {
                          "fill-rule": "evenodd",
                          d: "M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z",
                          "clip-rule": "evenodd"
                        })
                      ], -1),
                      _createTextVNode(" Create Rule Group ")
                    ]))
                  ])
                ]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        })),
    _createVNode(BaseModal, {
      modelValue: showNewGroupModal.value,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showNewGroupModal).value = $event))
    }, {
      title: _withCtx(() => _cache[28] || (_cache[28] = [
        _createTextVNode("Create New Rule Group")
      ])),
      footer: _withCtx(() => [
        _createElementVNode("button", {
          onClick: createRuleGroup,
          disabled: !newGroupName.value.trim(),
          class: "inline-flex w-full justify-center rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 sm:ml-3 sm:w-auto"
        }, " Create Group ", 8, _hoisted_48),
        _createElementVNode("button", {
          onClick: _cache[4] || (_cache[4] = ($event: any) => (showNewGroupModal.value = false)),
          class: "mt-3 inline-flex w-full justify-center rounded-lg border border-gray-700 px-3 py-2 text-sm font-medium text-gray-300 shadow-sm hover:bg-gray-700/50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:mt-0 sm:w-auto"
        }, " Cancel ")
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", null, [
          _cache[29] || (_cache[29] = _createElementVNode("label", {
            for: "groupName",
            class: "block text-sm font-medium text-gray-200"
          }, " Group Name ", -1)),
          _withDirectives(_createElementVNode("input", {
            id: "groupName",
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((newGroupName).value = $event)),
            type: "text",
            class: "mt-1 block w-full rounded-lg border border-gray-700 bg-dark px-3 py-2 text-sm text-gray-100 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary",
            placeholder: "Enter group name"
          }, null, 512), [
            [_vModelText, newGroupName.value]
          ])
        ])
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})